import { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Custom hook for managing UI preferences: mood/theme, text size,
 * layout, input mode, favorites, quick actions, smart templates.
 */
export const useUIPreferences = (MOOD_THEMES) => {
  // Mood state
  const [currentMoodIndex, setCurrentMoodIndex] = useState(() => {
    const saved = localStorage.getItem('mediscribe_mood');
    if (saved) {
      const index = MOOD_THEMES.findIndex(m => m.id === saved);
      return index >= 0 ? index : 1;
    }
    return 1;
  });

  // Text size: 0 = normal, 1 = medium (150%), 2 = large (200%)
  const [textSizeLevel, setTextSizeLevel] = useState(0);
  const textSizeClass = textSizeLevel === 0 ? '' : textSizeLevel === 1 ? 'text-size-medium' : 'text-size-large';

  // Layout preference: force single column even on large screens.
  // Feb 24, 2026 — also auto-force single column when the viewport is in
  // PORTRAIT orientation (h > w). Two-column on a vertical screen jumbles
  // headers, tiles, and the toolbar at 100%/150% zoom. The user's stored
  // preference is honored when landscape; portrait overrides until rotated.
  const _isPortrait = () => typeof window !== 'undefined' && window.innerHeight > window.innerWidth;
  const [userPrefSingleColumn, setUserPrefSingleColumn] = useState(() => {
    return localStorage.getItem('writeright_single_column') === 'true';
  });
  const [isPortraitViewport, setIsPortraitViewport] = useState(_isPortrait);
  const forceSingleColumn = userPrefSingleColumn || isPortraitViewport;
  const setForceSingleColumn = setUserPrefSingleColumn;

  // Track if on large screen (for section navigation in 1-column mode)
  const [isLargeScreen, setIsLargeScreen] = useState(() => window.innerWidth >= 1024);

  // Mar 2, 2026 — Reading width preference for single-column view.
  // 'narrow' = 900 / 1000 / 1120 across text sizes (comfortable reading, more margin)
  // 'comfortable' = 1240 / 1400 / 1560 (default — balanced content vs margin)
  // 'wide' = 1600 / 1700 / 1800 (max content, minimal gutter — providers on wide monitors)
  const [readingWidth, setReadingWidthRaw] = useState(() => {
    const v = localStorage.getItem('writeright_reading_width');
    return v === 'narrow' || v === 'comfortable' || v === 'wide' ? v : 'comfortable';
  });
  const setReadingWidth = useCallback((v) => {
    if (v !== 'narrow' && v !== 'comfortable' && v !== 'wide') return;
    setReadingWidthRaw(v);
    localStorage.setItem('writeright_reading_width', v);
  }, []);
  const readingWidthPx = useMemo(() => {
    // Returns the pixel maxWidth for the current textSizeLevel + reading width.
    // Kept as a memoized helper so App.js can use it inline without recomputing.
    const table = {
      narrow:      [900, 1000, 1120],
      comfortable: [1240, 1400, 1560],
      wide:        [1600, 1700, 1800],
    };
    const row = table[readingWidth] || table.comfortable;
    const idx = Math.min(2, Math.max(0, textSizeLevel));
    return row[idx];
  }, [readingWidth, textSizeLevel]);

  // Update screen size + portrait flag on resize / orientation change.
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
      setIsPortraitViewport(_isPortrait());
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Save layout preference. Portrait override is non-persistent and only
  // applies for the current orientation; rotating to landscape restores the
  // user's saved choice.
  const toggleColumnLayout = useCallback(() => {
    if (isPortraitViewport) {
      toast.info('Two-column view requires a landscape (wider-than-tall) screen. Rotate to landscape and try again.', { duration: 3500 });
      return;
    }
    setUserPrefSingleColumn(prev => {
      const newValue = !prev;
      if (!newValue && window.innerWidth < 1024) {
        toast.warning('Two-column view works best on screens wider than 1024px.', { duration: 4000 });
      }
      localStorage.setItem('writeright_single_column', newValue.toString());
      toast.success(newValue ? 'Switched to 1 column' : 'Switched to 2 columns', { duration: 1500 });
      return newValue;
    });
  }, [isPortraitViewport]);

  const [favoriteMoods, setFavoriteMoods] = useState(() => {
    const saved = localStorage.getItem('mediscribe_favorite_moods');
    if (saved) {
      try { return JSON.parse(saved); } catch { return []; }
    }
    return [];
  });

  const currentMood = MOOD_THEMES[currentMoodIndex];

  // Save favorite moods to localStorage
  useEffect(() => {
    localStorage.setItem('mediscribe_favorite_moods', JSON.stringify(favoriteMoods));
  }, [favoriteMoods]);

  // Apply mood theme to document
  useEffect(() => {
    MOOD_THEMES.forEach(mood => {
      document.documentElement.classList.remove(mood.class);
    });
    document.documentElement.classList.add(currentMood.class);
    localStorage.setItem('mediscribe_mood', currentMood.id);
  }, [currentMood, MOOD_THEMES]);

  // Input mode
  const [inputMode, setInputMode] = useState(() => {
    return localStorage.getItem('writeright_default_input_mode') || 'voice';
  });

  // Settings & panels
  const [showSettings, setShowSettings] = useState(false);
  const [showMoodInfo, setShowMoodInfo] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Quick Actions
  const [quickActions, setQuickActions] = useState(() => {
    const saved = localStorage.getItem('writeright_quick_actions');
    if (saved) {
      try { return JSON.parse(saved); } catch { return { imaging: [], referrals: [] }; }
    }
    return { imaging: [], referrals: [] };
  });
  const [showQuickActions, setShowQuickActions] = useState(false);

  // Smart Templates
  const [smartTemplates, setSmartTemplates] = useState({ templates: [], stats: null });
  const [showSmartTemplates, setShowSmartTemplates] = useState(false);
  const [isLoadingSmartTemplates, setIsLoadingSmartTemplates] = useState(false);

  return {
    // Mood
    currentMoodIndex, setCurrentMoodIndex, currentMood, favoriteMoods, setFavoriteMoods,
    showMoodInfo, setShowMoodInfo,
    // Text & Layout
    textSizeLevel, setTextSizeLevel, textSizeClass,
    forceSingleColumn, setForceSingleColumn, isLargeScreen,
    toggleColumnLayout,
    readingWidth, setReadingWidth, readingWidthPx,
    // Input & Settings
    inputMode, setInputMode, showSettings, setShowSettings, showAdvanced, setShowAdvanced,
    // Quick Actions
    quickActions, setQuickActions, showQuickActions, setShowQuickActions,
    // Smart Templates
    smartTemplates, setSmartTemplates, showSmartTemplates, setShowSmartTemplates,
    isLoadingSmartTemplates, setIsLoadingSmartTemplates,
  };
};

export default useUIPreferences;
